import {
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { SearchFilterModel } from "../models/SearchFilter-model";
import { FileService } from "../service/FileService.service";
import { DashboardPeriodService } from "../dashboard/services/dashboard-period.service";
import { fadeInAnimation } from "../animations/fadeInAnimation";
import { CurrentPartner, PartnersService } from "../service/Partners.service";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { MatDialog } from "@angular/material/dialog";
import { AiImportDialogComponent, AiImportDialogData } from "../ai-finisher/ai-import-dialog/ai-import-dialog.component";
import { FileServiceRecordModel } from "../models/FileServiceRecord-model";

@Component({
  selector: "app-file-service-table",
  templateUrl: "./file-service-table.component.html",
  styleUrls: ["./file-service-table.component.css"],
  animations: [fadeInAnimation],
  host: { "[@fadeInAnimation]": "" },
})
export class FileServiceTableComponent implements OnInit{
  public filterCount = signal<number>(0);
  public isInitialLoad = signal<boolean>(true);

  fileService = inject(FileService);
  periodService = inject(DashboardPeriodService);
  partnersService = inject(PartnersService);
  dialog = inject(MatDialog);
  router = inject(Router);
  route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  elRef = inject(ElementRef);
  @ViewChild(InfiniteScrollDirective) infiniteScrollDirective: {
    disposeScroller: { unsubscribe: () => void };
    setup: () => void;
  };

  constructor(@Inject("LOCAL_FILE_KEY") private fileServiceFilter: string) {}
  ngOnInit() {
    this.partnersService.currentPartner$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onRefreshTable());

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.loadFromRoute(params));

    setTimeout(() => {
      this.isInitialLoad.set(false);
    }, 1000);
  }

  private loadFromRoute(params: ParamMap): void {
    this.restartTableValue();
    this.getTableHeaderNames();

    const tuningNames = params.getAll('tuningNames').map(name => name.trim()).filter(Boolean);
    const startDate = params.get('startDate');
    const endDate = params.get('endDate');

    if (tuningNames.length > 0 && startDate && endDate) {
      this.updateFilterCount(0);
      this.fileService.activateTuningDrilldown({tuningNames, startDate, endDate});
      return;
    }

    this.fileService.clearTuningDrilldown();
    this.fileService.status.set(params.get('status') ?? '');
    this.fileService.startDate.set(startDate ?? undefined);
    this.fileService.endDate.set(endDate ?? undefined);
    // A dashboard date range takes precedence over filters saved for the normal table.
    if (!startDate || !endDate) {
      const savedFilters = this.fileService.getSavedFilters();
      if (savedFilters.length > 0) {
        this.fileService.selectedFilters.set(savedFilters);
        this.updateFilterCount(savedFilters.length);
        this.getFilteredRecords(savedFilters);
        return;
      }
    }
    this.fileService.selectedFilters.set([]);
    this.updateFilterCount(0);

    switch (this.partnersService.currentPartner) {
      case CurrentPartner.DynoChipTuningFiles:
        this.getAllFileServiceRecords();
        break;
      case CurrentPartner.ATMChipTunings:
        this.getAllImportsForPartners();
        break;
    }
  }

  clearTuningFilter(): void {
    this.router.navigate(['/app/file-service']);
  }

  getFilteredRecords(filters: SearchFilterModel[]) {
    this.fileService.fileServiceTableIsLoading.set(true);
    this.fileService
      .triggerListImportsFilteredRequest(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.fileService.fileServiceRecords.set(response);
        this.fileService.fileServiceTableIsLoading.set(false);
      });
  }

  restartTableValue() {
    this.fileService.clearErrorState();
    this.fileService.restartTableValue();
  }

  getAllFileServiceRecords() {
    this.fileService.getAllFileServiceRecords();
  }

  getAllImportsForPartners() {
    this.fileService.getAllImportsForPartners();
  }

  getTableHeaderNames() {
    this.fileService.getTableHeaderNames();
  }

  deleteItemFromTable(id: number, tableName: string) {
    this.fileService.deleteItemFromTable(id, tableName);
  }

  downloadFileFromTable(importId: number, fileName: string) {
    this.fileService.downloadFileFromTable(importId, fileName);
  }

  onRefreshTable() {
    if (this.fileService.tuningDrilldown()) {
      this.fileService.reloadTuningDrilldown();
      return;
    }

    this.fileService.clearErrorState();
    if (this.fileService.startDate()) {
      this.fileService.endDate.set(this.periodService.todayUntilNow().end);
    }
    this.fileService.onRefreshTable();
  }

  openPureOriginalMatchingDetails(id: number) {
    this.router.navigate(["app/pure-originals/pure-original-details"], {
      queryParams: { pureOriginalId: id },
    });
  }

  /** The row's AI icon: everything the AI Finisher did for this import — reason, confidence, download, approve/reject. */
  openAiWorkDialog(element: FileServiceRecordModel) {
    this.dialog.open(AiImportDialogComponent, {
      width: "1060px",
      maxWidth: "96vw",
      maxHeight: "92vh",
      panelClass: "ai-import-dialog-panel",
      data: { importId: +element.importid, filename: element.filename } as AiImportDialogData,
    });
  }

  openAdvancedSearchSettings() {
    this.fileService.openAdvancedSearchSettings();
  }

  selectFilters(event: SearchFilterModel[]) {
    this.fileService.changeSelectFilters(event);
  }

  onAdvancedSettingClosed($event: boolean) {
    this.fileService.closeAdvancedSettingsModal($event.valueOf());
  }

  onSearchInput($event: any) {
    this.fileService.onSearchInput($event.valueOf());
  }
  onScroll() {
    this.fileService.onScroll();
  }
  onScrolledUp() {
    this.infiniteScrollDirective.disposeScroller.unsubscribe();
    this.infiniteScrollDirective.setup();
  }
  changeFileServiceTableSortDirection($event: any) {
    const tableElement: HTMLElement =
      this.elRef.nativeElement.querySelector(".table-container");
    this.fileService.changeFileServiceTableSortDirection($event, tableElement);
  }
  isTableEmpty() {
    return this.fileService.isTableEmpty();
  }
  updateFilterCount(count: number) {
    this.filterCount.set(count);
  }

  formatDate(value: any, column: string): string {
    if (value == null || value === '') return '-';

    const dateColumns = ['date', 'dateUploaded', 'dateUpdated', 'dateExported', 'dateMatched', 'versionDateUploaded', 'dateuploaded'];
    if (dateColumns.includes(column)) {
      const parts = value.toString().split('-');
      if (parts.length < 6) return value;

      let year, month, day, hours, minutes, seconds;

      // Detect format: MM-DD-YYYY vs DD-MM-YYYY
      // If the first part is > 12, it's definitely Day (DD-MM-YYYY)
      // If the second part is > 12, it's definitely Day (MM-DD-YYYY)
      if (parseInt(parts[0], 10) > 12) {
        // DD-MM-YYYY
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10) - 1;
        year = parseInt(parts[2], 10);
      } else if (parseInt(parts[1], 10) > 12) {
        // MM-DD-YYYY
        month = parseInt(parts[0], 10) - 1;
        day = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
      } else {
        // Fallback to DD-MM-YYYY or handle ambiguity. 
        // Based on the user query, the "different" format is MM-DD-YYYY
        // Let's assume MM-DD-YYYY if it doesn't match the standard DD-MM-YYYY logic
        // but only for the specific 'dateuploaded' column if needed.
        // Actually, the previous ones were DD-MM-YYYY.
        if (column === 'dateuploaded') {
          month = parseInt(parts[0], 10) - 1;
          day = parseInt(parts[1], 10);
        } else {
          day = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10) - 1;
        }
        year = parseInt(parts[2], 10);
      }

      hours = parseInt(parts[3], 10);
      minutes = parseInt(parts[4], 10);
      seconds = parseInt(parts[5], 10);

      const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));

      if (isNaN(utcDate.getTime())) return value;

      const localDay = utcDate.getDate().toString().padStart(2, '0');
      const localMonth = (utcDate.getMonth() + 1).toString().padStart(2, '0');
      const localYear = utcDate.getFullYear();
      const localHours = utcDate.getHours().toString().padStart(2, '0');
      const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
      const localSeconds = utcDate.getSeconds().toString().padStart(2, '0');

      return `${localDay}-${localMonth}-${localYear} ${localHours}:${localMinutes}:${localSeconds}`;
    }

    return value;
  }
}
